/**
 * Sistemdeki "canlı" Three.js asset kümesini izleyen merkezi registry:
 * geometry / material / texture. Amaç: hangi kaynaktan gelirse gelsin (loader,
 * prosedürel kod, editör) her asset'i tek yerde görebilmek; kim kullanıyor,
 * kaç tane yaşıyor sorularını cevaplayabilmek.
 *
 * Neden constructor patch DEĞİL: three'nin alt sınıfları (BoxGeometry,
 * MeshStandardMaterial, DataTexture…) base constructor'ı paketin kendi içinden
 * çağırır; dışarıdan sarmalanan base constructor'a uğramaz. Bunun yerine her
 * zaman INSTANCE okuruz (alt sınıf farkı yok):
 *   - sahneye eklenenler   → scene-mutated'da otomatik track(activeScene)
 *   - loader ile yüklenenler→ loader prototype patch'i (assetTracking.ts) track eder
 * İkisi de geliştiriciden bağımsız çalışır; kimsenin elle track çağırması gerekmez.
 *
 * Bellek: registry asset'leri WEAK tutar (Map değerleri `WeakRef`). Yani registry
 * hiçbir asset'i bellekte canlı tutmaz (GC'yi engellemez). İki temizlik yolu:
 *   - `dispose()` çağrılınca → dispose event'i ile ANINDA düşer (deterministik).
 *   - dispose edilmeden bırakılıp GC olursa → `FinalizationRegistry` callback'i
 *     ölü `uuid` girdisini temizler (gecikmeli backstop; zamanlama belirsiz).
 * Okuma sırasında (`list`/`getStats`) ölü WeakRef'ler deref edilip ayıklanır.
 */
import type { BufferGeometry, Material, Object3D, Texture } from "three";

export type AssetKind = "geometry" | "material" | "texture";

export interface AssetEntry {
  kind: AssetKind;
  uuid: string;
  name: string;
  /** three tip adı (örn. "MeshStandardMaterial", "BoxGeometry"). */
  type: string;
  ref: object;
}

interface Disposable {
  uuid?: string;
  name?: string;
  type?: string;
  addEventListener?: (type: "dispose", cb: () => void) => void;
}

type AssetMap = Map<string, WeakRef<Disposable>>;

const geometries: AssetMap = new Map();
const materials: AssetMap = new Map();
const textures: AssetMap = new Map();

// Aynı nesneye iki kez dispose-dinleyici/finalizer bağlamamak için. WeakSet
// referansı canlı tutmaz (GC'yi engellemez).
const wired = new WeakSet<object>();

// Asset dispose EDİLMEDEN bırakılıp GC olursa, ilgili map'ten ölü uuid girdisini
// temizler. heldValue asset'e referans VERMEZ (yoksa pinler) — sadece map + uuid.
const finalizer = new FinalizationRegistry<{ map: AssetMap; uuid: string }>(
  ({ map, uuid }) => map.delete(uuid)
);

export class AssetRegistry {
  /**
   * Bir asset graph'ını (Object3D, gltf.scene, tek mesh…) gezip içindeki tüm
   * geometry/material/texture'ları kaydeder. İdempotent.
   * `userData.__editorHidden` işaretli alt-ağaçlar (grid/ışık/gizmo gibi editör
   * yardımcıları) budanır — yalnızca gerçek içerik asset'leri izlenir.
   */
  static track(root: Object3D | null | undefined): void {
    this._walk(root, (o) => {
      const mesh = o as {
        geometry?: BufferGeometry;
        material?: Material | Material[];
      };
      if (mesh.geometry) this.trackGeometry(mesh.geometry);
      const mats = Array.isArray(mesh.material)
        ? mesh.material
        : mesh.material
          ? [mesh.material]
          : [];
      for (const m of mats) this.trackMaterial(m);
    });
  }

  static trackMaterial(material: Material): void {
    this._add(materials, material as unknown as Disposable);
    // Material üzerindeki tüm texture-tipli alanları (map, normalMap, …) topla.
    for (const key in material) {
      const v = (material as unknown as Record<string, unknown>)[key] as {
        isTexture?: boolean;
      } & Disposable;
      if (v?.isTexture) this._add(textures, v);
    }
  }

  static trackGeometry(geometry: BufferGeometry): void {
    this._add(geometries, geometry as unknown as Disposable);
  }

  static trackTexture(texture: Texture): void {
    this._add(textures, texture as unknown as Disposable);
  }

  static getStats(): {
    geometries: number;
    materials: number;
    textures: number;
  } {
    let g = 0;
    let m = 0;
    let t = 0;
    this._alive(geometries, () => g++);
    this._alive(materials, () => m++);
    this._alive(textures, () => t++);
    return { geometries: g, materials: m, textures: t };
  }

  /**
   * Ölü (deref edilemeyen) WeakRef girdilerini ANINDA tarayıp siler; kaç adet
   * temizlendiğini döndürür. FinalizationRegistry callback'i elle tetiklenemez
   * (yalnızca GC tetikler) — bu, aynı temizliğin senkron/deterministik
   * karşılığıdır. Mümkünse önce GC'yi dürtürüz (Chrome `--js-flags=--expose-gc`
   * ile açıksa) ki dispose'suz bırakılmış asset'ler de toplanıp ayıklanabilsin.
   */
  static prune(): {
    geometries: number;
    materials: number;
    textures: number;
    gc: boolean;
  } {
    const gc = (globalThis as { gc?: () => void }).gc;
    gc?.();
    return {
      geometries: this._sweep(geometries),
      materials: this._sweep(materials),
      textures: this._sweep(textures),
      gc: !!gc,
    };
  }

  /** Tüm canlı asset'leri tek liste olarak döndürür (inspector için). */
  static list(): AssetEntry[] {
    const out: AssetEntry[] = [];
    const dump = (kind: AssetKind, map: AssetMap) =>
      this._alive(map, (a) =>
        out.push({
          kind,
          uuid: a.uuid!,
          name: a.name || a.type || kind,
          type: a.type || kind,
          ref: a as object,
        })
      );
    dump("geometry", geometries);
    dump("material", materials);
    dump("texture", textures);
    return out;
  }

  /**
   * Bir asset graph'ını gezerek "asset.uuid -> onu kullanan Object3D'ler" haritası
   * kurar. "Kim kullanıyor" + "hiyerarşide bul" için kaynak. Yalnızca verilen
   * root altındaki (örn. sahnedeki) kullanıcıları görür.
   */
  static buildUsageMap(root: Object3D | null | undefined): Map<string, Object3D[]> {
    const map = new Map<string, Object3D[]>();
    const add = (uuid: string | undefined, o: Object3D) => {
      if (!uuid) return;
      (map.get(uuid) ?? map.set(uuid, []).get(uuid)!).push(o);
    };
    this._walk(root, (o) => {
      const mesh = o as {
        geometry?: BufferGeometry;
        material?: Material | Material[];
      };
      add(mesh.geometry?.uuid, o);
      const mats = Array.isArray(mesh.material)
        ? mesh.material
        : mesh.material
          ? [mesh.material]
          : [];
      for (const m of mats) {
        add(m?.uuid, o);
        for (const key in m) {
          const v = (m as unknown as Record<string, unknown>)[key] as {
            isTexture?: boolean;
            uuid?: string;
          };
          if (v?.isTexture) add(v.uuid, o);
        }
      }
    });
    return map;
  }

  // --- internals ---

  /**
   * `traverse` benzeri ama `userData.__editorHidden` işaretli düğümde alt-ağacı
   * budar (o düğüm ve altındakiler ziyaret edilmez). Editör yardımcılarının
   * asset'lerini izleme/sayım dışında tutar.
   */
  private static _walk(
    root: Object3D | null | undefined,
    cb: (o: Object3D) => void
  ): void {
    if (!root) return;
    if (root.userData?.__editorHidden) return;
    cb(root);
    const children = root.children;
    for (let i = 0; i < children.length; i++) this._walk(children[i], cb);
  }

  /**
   * Weak kaydet + ilk kez ise iki temizlik kancası bağla:
   *   - dispose event'i → anında map'ten düş (deterministik).
   *   - FinalizationRegistry → dispose'suz GC olursa ölü girdiyi temizle.
   */
  private static _add(map: AssetMap, asset: Disposable): void {
    if (!asset?.uuid) return;
    map.set(asset.uuid, new WeakRef(asset));
    if (wired.has(asset as object)) return;
    wired.add(asset as object);
    asset.addEventListener?.("dispose", () => map.delete(asset.uuid!));
    finalizer.register(asset as object, { map, uuid: asset.uuid });
  }

  /** Map'i gez; ölü WeakRef'leri ayıkla (ve map'ten sil), canlıları cb'ye ver. */
  private static _alive(map: AssetMap, cb: (asset: Disposable) => void): void {
    map.forEach((ref, uuid) => {
      const asset = ref.deref();
      if (asset) cb(asset);
      else map.delete(uuid);
    });
  }

  /** Ölü WeakRef girdilerini siler; silinen adedi döndürür. */
  private static _sweep(map: AssetMap): number {
    let removed = 0;
    map.forEach((ref, uuid) => {
      if (!ref.deref()) {
        map.delete(uuid);
        removed++;
      }
    });
    return removed;
  }
}
